import { useRoute } from '@react-navigation/native';
import { getAuth, signInWithCustomToken, signOut } from 'firebase/auth';
import React, { useEffect, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';

import { Text } from '@/components/ui';
import { exchangeShopCode } from '@/features/shop/services/shop-link';
import { getFirebaseApp } from '@/libs/firebase/init.web';
import { captureException } from '@/libs/logger';
import { useOidcInteractionStore } from '@/stores';
import { colors } from '@/theme/colors';

/**
 * The code currently being handled, at MODULE scope (survives remounts). `signInWithCustomToken`
 * flips the web app to authenticated, which remounts this screen; a per-instance ref wouldn't
 * survive that, so each remount would re-`exchangeShopCode` the same one-time code — the first call
 * consumes it and every later call 401s, flashing a false error page (Sentry 2026-07-22). Keyed by
 * code so a genuinely new tap (new code) is never skipped. No reset on failure: remounts only
 * happen *after* a successful sign-in (we're already navigating away), so a failed attempt never
 * remounts and its error surfaces normally.
 */
let processedCode: string | null = null;

/**
 * Shop autologin bridge (web-only). The mobile app opens `/shop-bridge?code=…` in the system
 * browser. Here we swap the one-time code for a Firebase custom token, sign the fresh web session
 * in as the app's account (silently), then follow the IdP's `shopifyLoginUrl` — which force-clears
 * any stale OIDC session and bounces through Shopify → OIDC → the store, now logged in.
 *
 * We flag the OIDC-interaction gate active so `withUnAuth` keeps this screen mounted when
 * `signInWithCustomToken` flips Firebase to authenticated, instead of resetting to the dashboard
 * before we navigate away.
 */
export function ShopBridgeScreen() {
  const route = useRoute();
  const code = ((route.params ?? {}) as { code?: string }).code ?? '';
  const setActive = useOidcInteractionStore(state => state.setActive);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    setActive(true);
    return () => setActive(false);
  }, [setActive]);

  useEffect(() => {
    if (!code) {
      setErrored(true);
      return;
    }
    // Already handling this code (a post-sign-in remount) — navigation is imminent; do nothing.
    if (processedCode === code) return;
    processedCode = code;
    (async () => {
      try {
        const { customToken, shopifyLoginUrl } = await exchangeShopCode(code);
        const auth = getAuth(getFirebaseApp());
        // Clear any previous account first so we always sign in as the app's current account.
        await signOut(auth).catch(() => undefined);
        await signInWithCustomToken(auth, customToken);
        window.location.assign(shopifyLoginUrl);
      } catch (error) {
        captureException({ error_payload: error }, { tags: { usk_log_type: 'shop_bridge_error' } });
        setErrored(true);
      }
    })();
  }, [code]);

  return (
    <View style={styles.center}>
      {errored ? (
        <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.label}>
          Something went wrong opening the shop. Please return to the app and try again.
        </Text>
      ) : (
        <>
          <ActivityIndicator size="large" color={colors['10']} />
          <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.label}>
            Opening the shop…
          </Text>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
  label: { marginTop: 16, textAlign: 'center', color: colors['10'] },
});

export default ShopBridgeScreen;
